import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { MemberItem } from "@components/ui/blocks/MemberAssignmentBlock";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { MemberModal } from "@components/modals/MemberModal";
import { AddMemberModal } from "@components/modals/AddMemberModal";
import BaseModal from "@components/modals/BaseModal";
import DeleteUserModal from "@components/modals/DeleteUserModal";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { role_dict } from "@screens/sampleData";
import { getHistoryDateFromFlutter } from "@services/timeHelpers";
import { useDataStore } from "@store/user_org";
import { useState } from "react";
import { usersApi } from "@services/api";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";

export const Team = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const isLoadingOrgMembers = useDataStore(
    (state) => state.isLoadingOrgMembers,
  );
  const fetchOrgMembers = useDataStore((state) => state.fetchOrgMembers);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInactivesModal, setShowInactivesModal] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);

  const inactiveMembers = orgMembers.filter(
    (u) => u.config?.access_type !== "GUEST" && u.status === "inactive",
  );

  const handleReactivate = async (userId: string, userName: string) => {
    setReactivatingId(userId);
    try {
      await usersApi.reactivate(userId);
      if (orgData?.id) await fetchOrgMembers(orgData.id);
      showNotification({
        type: "success",
        message: `${userName} a été réactivé`,
      });
    } catch {
      showNotification({
        type: "error",
        message: "Échec de la réactivation du membre",
      });
    } finally {
      setReactivatingId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await usersApi.delete(deleteTarget.id);
      // Refresh the members list
      if (orgData?.id) {
        await fetchOrgMembers(orgData.id);
      }
      showNotification({
        type: "success",
        message: `${deleteTarget.name} a été supprimé avec succès`,
      });
      setDeleteTarget(null);
    } catch (error) {
      console.error("Failed to delete user:", error);
      showNotification({
        type: "error",
        message: "Échec de la suppression du membre",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      {selectedMember && (
        <MemberModal
          memberId={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
      {showAddMember && (
        <AddMemberModal
          onClose={() => setShowAddMember(false)}
          onSuccess={() => {
            // Refresh members list after successful invitation
            if (orgData?.id) {
              fetchOrgMembers(orgData.id);
            }
          }}
        />
      )}
      {deleteTarget && (
        <DeleteUserModal
          userName={deleteTarget.name}
          onConfirm={handleDeleteUser}
          onClose={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
      {showInactivesModal && (
        <BaseModal
          title="Membres inactifs"
          startIcon={<Icon1 name="archive" size={20} />}
          onClose={() => setShowInactivesModal(false)}
        >
          <div className="flex flex-col gap-1 p-4 max-h-96 overflow-y-auto">
            {inactiveMembers.length === 0 ? (
              <div className="text-h4 text-neutral-400 py-4 text-center">
                Aucun membre inactif.
              </div>
            ) : (
              inactiveMembers
                .sort((a, b) =>
                  (a.infos.full_name || a.infos.display_name || "").localeCompare(
                    b.infos.full_name || b.infos.display_name || "",
                  ),
                )
                .map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-row items-center justify-between gap-3 p-2 rounded-lg hover:bg-neutral-50"
                  >
                    <div className="flex flex-row items-center gap-3 min-w-0 flex-1">
                      <UserAvatar
                        src={user.customization?.photo_url}
                        className="w-9 h-9 rounded-full border flex-shrink-0 opacity-70"
                        value={user.customization?.avatar_seed || user.infos.display_name}
                        size={36}
                      />
                      <div className="flex flex-col min-w-0">
                        <div className="text-h3 font-medium text-neutral-700 truncate">
                          {user.infos.full_name || user.infos.display_name}
                        </div>
                        <div className="text-h5 text-neutral-400 truncate">
                          {role_dict[user.role as keyof typeof role_dict]}
                          {user.infos.email ? ` · ${user.infos.email}` : ""}
                        </div>
                      </div>
                    </div>
                    <SecondaryButton
                      variant="small"
                      label={reactivatingId === user.id ? "..." : "Réactiver"}
                      onClick={() =>
                        handleReactivate(
                          user.id,
                          user.infos.full_name || user.infos.display_name || "Ce membre",
                        )
                      }
                      disabled={reactivatingId === user.id}
                    />
                  </div>
                ))
            )}
          </div>
          <div className="border-t p-4 flex justify-end">
            <SecondaryButton
              variant="small"
              label="Fermer"
              onClick={() => setShowInactivesModal(false)}
            />
          </div>
        </BaseModal>
      )}
      <div className="flex flex-col  gap-4  ">
        <div className="flex flex-col   ">
          <Text1>Equipe</Text1>
          <Text2>Gérez les membres de votre équipe ici.</Text2>
          <Text3>
            Ajoutez, modifiez et supprimez les membres de votre équipe pour
            contrôler l'accès et les permissions au sein de votre organisation.
          </Text3>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="flex flex-row justify-between items-center mb-2">
            <div className="text-h2 font-semibold text-black">
              Membres de l'organisation ({orgMembers.filter((u) => u.config?.access_type !== "GUEST" && u.status !== "inactive").length})
            </div>
            <div className="flex flex-row items-center gap-2">
              {isOwnerOrAdmin(userData) && inactiveMembers.length > 0 && (
                <button
                  onClick={() => setShowInactivesModal(true)}
                  className="text-h4 text-neutral-500 hover:text-neutral-800 underline underline-offset-2"
                >
                  Inactifs ({inactiveMembers.length})
                </button>
              )}
              {isOwnerOrAdmin(userData) && (
                <SecondaryButton
                  variant="large"
                  label="Ajouter un membre"
                  startIcon={<Icon1 name="plus" size={16} />}
                  onClick={() => setShowAddMember(true)}
                />
              )}
            </div>
          </div>

          {isLoadingOrgMembers ? (
            <div className="text-h4 text-neutral-400 text-center py-8">
              Chargement des membres...
            </div>
          ) : orgMembers.filter((u) => u.config?.access_type !== "GUEST" && u.status !== "inactive").length === 0 ? (
            <div className="text-h4 text-neutral-400 text-center py-8">
              Aucun membre dans l'organisation
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-neutral-200">
              {orgMembers
                .filter((u) => u.config?.access_type !== "GUEST" && u.status !== "inactive")
                .sort((a, b) => {
                  if (a.id === userData?.id) return -1;
                  if (b.id === userData?.id) return 1;
                  return (
                    a.infos.full_name || a.infos.display_name
                  ).localeCompare(b.infos.full_name || b.infos.display_name);
                })
                .map((user) => {
                  const isPending = user.status === "pending";
                  const statusColor = isPending
                    ? "bg-yellow-300 ring-yellow-200"
                    : user.activity?.connected
                      ? "bg-emerald-300 ring-emerald-200"
                      : "bg-neutral-200 ring-neutral-100";
                  const statusTextColor = isPending
                    ? "text-yellow-600"
                    : user.activity?.connected
                      ? "text-emerald-400"
                      : "text-neutral-400";
                  const statusText = isPending
                    ? "En attente"
                    : user.activity?.connected
                      ? "En ligne"
                      : user.activity?.last_active_time
                        ? `Actif ${getHistoryDateFromFlutter(user.activity.last_active_time)}`
                        : "Jamais connecté";

                  return (
                    <div
                      key={user.id}
                      className="flex flex-row justify-between items-center p-2"
                    >
                      <div className="flex flex-row items-center gap-3 flex-1 min-w-0">
                        <UserAvatar
                          src={user.customization?.photo_url}
                          className="w-12 h-12 rounded-full border flex-shrink-0"
                          value={user.customization?.avatar_seed || user.infos.display_name}
                          size={48}
                        />
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="text-h2 font-semibold truncate">
                            {user.infos.full_name || user.infos.display_name}
                            {user.id === userData?.id && (
                              <span className="text-h4 text-neutral-400 font-normal ml-2">
                                (Vous)
                              </span>
                            )}
                          </div>
                          <div className="text-h5 text-neutral-400 font-semibold">
                            {role_dict[user.role as keyof typeof role_dict]}
                          </div>
                          <div className="text-h5 text-neutral-400 truncate">
                            {user.infos.email}
                          </div>
                          <div className={`flex flex-row gap-1 items-center mt-0.5 ${statusTextColor}`}>
                            <div className={`w-1.5 h-1.5 ring-2 rounded-full flex-shrink-0 ${statusColor}`} />
                            <div className="text-h5">{statusText}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-row gap-4 items-center flex-shrink-0">
                        <div className="w-150">
                          <MemberItem
                            memberName={user.infos.display_name}
                            memberColor={user.customization.color_tag}
                            onClick={() => {}}
                          />
                        </div>
                        <SecondaryButton
                          disabled={
                            !isOwnerOrAdmin(userData) &&
                            userData?.id !== user.id
                          }
                          onClick={() => setSelectedMember(user.id)}
                          variant="small"
                          label="Gérer"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
